import React, { useEffect } from "react"

import Button from "@fuse/sharedComponents/buttons/SolidBtn"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import { showErrorToast } from "@fuse/sharedComponents/themeToast/toastUtils"
import FancyText from "@fuse/sharedComponents/typography/FancyText"
import { TWebUser } from "@letshang/constants"
import { size } from "lodash-es"
import { useRecoilState } from "recoil"

import { screenState, userState } from "@fuse/recoil/signup"

import { useSignup } from "@fuse/pages/signUp/controller"

const Name = () => {
  const [, setStep] = useRecoilState(screenState)

  const [user, setUser] = useRecoilState(userState)
  const { isLoading } = useSignup()

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUser((prev: TWebUser) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const validateName = (name: string): string | null => {
    if (!name || typeof name !== "string") {
      return "Name is required"
    }

    // Trim whitespace
    const trimmed = name.trim()

    // Check if empty after trim
    if (trimmed.length === 0) {
      return "Name cannot be empty or only spaces"
    }

    // Check minimum length
    if (trimmed.length < 2) {
      return "Name must be at least 2 characters long"
    }

    // Check maximum length
    if (trimmed.length > 50) {
      return "Name cannot exceed 50 characters"
    }

    // Check for numbers
    if (/\d/.test(trimmed)) {
      return "Name cannot contain numbers"
    }

    // Check for emojis (comprehensive emoji ranges)
    const emojiRegex =
      /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA70}-\u{1FAFF}]|[\u{E000}-\u{F8FF}]|[\u{FE00}-\u{FE0F}]|[\u{1F004}\u{1F0CF}\u{1F18E}\u{1F191}-\u{1F19A}\u{1F1E6}-\u{1F1FF}\u{1F201}\u{1F21A}\u{1F22F}\u{1F232}-\u{1F236}\u{1F238}-\u{1F23A}\u{1F250}\u{1F251}\u{1F300}-\u{1F320}\u{1F32D}-\u{1F335}\u{1F337}-\u{1F37C}\u{1F37E}-\u{1F393}\u{1F3A0}-\u{1F3CA}\u{1F3CF}-\u{1F3D3}\u{1F3E0}-\u{1F3F0}\u{1F3F4}\u{1F3F8}-\u{1F43E}\u{1F440}\u{1F442}-\u{1F4FC}\u{1F4FF}-\u{1F53D}\u{1F54B}-\u{1F54E}\u{1F550}-\u{1F567}\u{1F57A}\u{1F595}\u{1F596}\u{1F5A4}\u{1F5FB}-\u{1F64F}\u{1F680}-\u{1F6C5}\u{1F6CC}\u{1F6D0}-\u{1F6D2}\u{1F6EB}\u{1F6EC}\u{1F6F4}-\u{1F6FA}\u{1F7E0}-\u{1F7EB}\u{1F90D}-\u{1F93A}\u{1F93C}-\u{1F945}\u{1F947}-\u{1F971}\u{1F973}-\u{1F976}\u{1F97A}-\u{1F9A2}\u{1F9A5}-\u{1F9AA}\u{1F9AE}-\u{1F9CA}\u{1F9CD}-\u{1F9FF}\u{1FA70}-\u{1FA73}\u{1FA78}-\u{1FA7A}\u{1FA80}-\u{1FA82}\u{1FA90}-\u{1FA95}]/u
    if (emojiRegex.test(trimmed)) {
      return "Name cannot contain emojis"
    }

    // Check for brackets and other disallowed special characters
    if (/[(){}\[\]<>]/.test(trimmed)) {
      return "Name cannot contain brackets or special characters like (), [], {}, <>"
    }

    // Check for other problematic characters
    if (/[#@$%^*+=|\\\/`~]/.test(trimmed)) {
      return "Name cannot contain special characters like #, @, $, %, ^, *, +, =, |, \\, /, `, ~"
    }

    // Check for multiple consecutive spaces
    if (/\s{2,}/.test(trimmed)) {
      return "Name cannot have multiple consecutive spaces"
    }

    // Check if it contains only valid characters (Unicode letters, marks, apostrophes, hyphens, single spaces, dots, commas)
    if (!/^[\p{L}\p{M}'\-\s.,]+$/u.test(trimmed)) {
      return "Name contains invalid characters. Only letters, apostrophes, hyphens, spaces, and basic punctuation are allowed"
    }

    // Check that it has at least one letter (not just punctuation and spaces)
    if (!/\p{L}/u.test(trimmed)) {
      return "Name must contain at least one letter"
    }

    return null
  }

  const handleSubmit = () => {
    const error = validateName(user?.name || "")
    if (error) {
      showErrorToast(error)
    } else {
      setStep(4)
    }
  }

  return (
    <div className="flex justify-center items-center w-full h-full p-6">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <FancyText className="text-2xl font-bold text-white mb-2">
            What's your name?
          </FancyText>
          <p className="text-white/70">
            We'll use this to personalize your experience
          </p>
        </div>

        <div className="relative z-[10]">{isLoading && <LoadingSpinner />}</div>
        <div className=" relative rounded-2xl space-y-6">
          {/* Title */}

          <div className="space-y-4">
            <div className="relative">
              <input
                type="text"
                name="name"
                value={user?.name || ""}
                onChange={handleOnChange}
                className="w-full px-4 py-3 rounded-lg bg-white/20 text-white placeholder-white/50 border border-white/30 focus:border-white/50 focus:outline-none transition-colors"
                placeholder="Enter your full name"
                autoFocus
              />
            </div>

            <button
              className={`w-full bg-white/20 text-white py-3 rounded-full font-medium transition ${size(user?.name) < 3 ? "opacity-50" : ""}`}
              onClick={handleSubmit}
              disabled={size(user?.name) < 3}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Name
